import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CircleStop, Play, RefreshCw, XCircle } from "lucide-react";
import api from "../lib/api";
import "./BuildLincJobs.css";

const TERMINAL = new Set(["passed", "failed", "cancelled"]);

function statusIcon(status) {
  if (status === "passed") return <CheckCircle2 size={14} />;
  if (status === "failed" || status === "cancelled") return <XCircle size={14} />;
  return <RefreshCw size={14} />;
}

export default function BuildLincJobs({ projectId, branch, sha, onError }) {
  const [jobs, setJobs] = useState([]);
  const [profile, setProfile] = useState("build_and_test");
  const [launching, setLaunching] = useState(false);
  const active = jobs.some((job) => !TERMINAL.has(job.status));

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/buildlinc/jobs?limit=10`);
      setJobs(data?.jobs || []);
    } catch (error) {
      onError?.(error, "Could not load local build jobs.");
    }
  }, [projectId, onError]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(load, 2000);
    return () => window.clearInterval(timer);
  }, [active, load]);

  const launch = async () => {
    if (!projectId || !branch || !sha) return;
    setLaunching(true);
    try {
      await api.post(`/projects/${projectId}/buildlinc/jobs`, { branch, sha, profile });
      await load();
    } catch (error) {
      onError?.(error, "Could not launch the local build job.");
    } finally {
      setLaunching(false);
    }
  };

  const cancel = async (jobId) => {
    try {
      await api.post(`/projects/${projectId}/buildlinc/jobs/${jobId}/cancel`);
      await load();
    } catch (error) {
      onError?.(error, "Could not cancel the build job.");
    }
  };

  const latest = jobs[0];
  return (
    <article className="buildlinc-panel buildlinc-jobs-panel">
      <header><div><Play size={16} /><span><b>Local build + test runner</b><small>Exact-SHA isolated jobs; commands are fixed by the server.</small></span></div><em>{latest?.status || "idle"}</em></header>
      <div className="buildlinc-job-controls">
        <label><small>Allowlisted job</small><select value={profile} onChange={(event) => setProfile(event.target.value)}><option value="build_and_test">Frontend build + backend tests</option><option value="frontend_build">Frontend build</option><option value="backend_tests">Backend tests</option></select></label>
        <button className="btn primary" disabled={!sha || launching || active} onClick={launch}>{launching ? "Queueing…" : "Run candidate"}</button>
      </div>
      <div className="buildlinc-job-list">
        {jobs.length ? jobs.map((job) => (
          <section key={job.id} className={`buildlinc-job buildlinc-job-${job.status}`}>
            <div className="buildlinc-job-summary"><span>{statusIcon(job.status)}<b>{job.profile.replaceAll("_", " ")}</b></span><code>{job.sha.slice(0, 10)}</code><em>{job.status}</em>{!TERMINAL.has(job.status) ? <button className="btn small" onClick={() => cancel(job.id)}><CircleStop size={12} /> Cancel</button> : null}</div>
            <small>{job.started_at ? `Started ${new Date(job.started_at).toLocaleString()}` : `Queued ${new Date(job.created_at).toLocaleString()}`}{job.completed_at ? ` · finished ${new Date(job.completed_at).toLocaleString()}` : ""}{job.exit_code !== null && job.exit_code !== undefined ? ` · exit ${job.exit_code}` : ""}</small>
            {job.error ? <p>{job.error}</p> : null}
            {job.output ? <pre>{job.output}{job.output_truncated ? "\n[output truncated]" : ""}</pre> : null}
          </section>
        )) : <p className="buildlinc-hint">No local jobs have been launched for this project.</p>}
      </div>
    </article>
  );
}
